import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {Matrix4, rad2deg} from "@cm/math"
import {NumberNode} from "#template/nodes/number/number-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {TransformLike} from "#template/nodes/types"
import {transformLike} from "#template/nodes/transform/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {visitNone} from "#template/template-node/visit-helpers"

export type TransformValueType = "positionX" | "positionY" | "positionZ" | "rotationX" | "rotationY" | "rotationZ"

export type TransformToValueParameters = {
    name: string
    transform: TransformLike | null
    valueType: TransformValueType
}

const transformToValueDefinition: TemplateNodeDefinition<TransformToValueParameters> = {
    nodeClass: "TransformToValue",
    validation: {
        parameters: z.object({
            name: z.string(),
            transform: z.nullable(transformLike),
            valueType: z.enum(["positionX", "positionY", "positionZ", "rotationX", "rotationY", "rotationZ"]),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class TransformToValue extends NumberNode<TransformToValueParameters> {
    public override get definition() {
        return transformToValueDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<TransformToValueParameters> {
        if (this.parameters.transform === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<number | null> {
        return scope.pureLambda(
            scope.tuple(evaluator.evaluate(this.parameters.transform), this.parameters.valueType),
            ([transform, valueType]) => {
                if (!transform) return null

                const matrix = new Matrix4(transform)
                const {position, quaternion} = matrix.decompose()

                switch (valueType) {
                    case "positionX":
                        return position.x
                    case "positionY":
                        return position.y
                    case "positionZ":
                        return position.z
                }

                const euler = quaternion.toEuler()
                switch (valueType) {
                    case "rotationX":
                        return rad2deg(euler.x)
                    case "rotationY":
                        return rad2deg(euler.y)
                    case "rotationZ":
                        return rad2deg(euler.z)
                }

                throw new Error(`Unknown valueType: ${valueType}`)
            },
            "transformToValue",
        )
    }
}
